import { type FC, useEffect, useState } from 'react';
import Button from '@components/Button';
import InputSelect from '@components/InputSelect';
import InputText from '@components/InputText';
import InputTextarea from '@components/InputTextarea';
import type { DbEvalConfigRoot, EvalModelId } from '@root/domain/schemas/evals/eval-config.schema';
import type { UpdateEvalConfigMetaPayload } from '@root/domain/schemas/payload.schema';

import { cardBodyClasses, cardClasses, cardHeaderClasses, sectionHeadingClasses, subtleTextClasses } from '../classes';
import {
  DEFAULT_EVAL_MODEL_ID,
  EVAL_MODEL_ID_OPTIONS,
  getWorkflowPackExpectations,
  getWorkflowTypeLabel,
} from '../constants';

interface SettingsTabProps {
  root: DbEvalConfigRoot;
  isSaving: boolean;
  onSave: (payload: Omit<UpdateEvalConfigMetaPayload, 'operation'>) => Promise<void>;
}

export const SettingsTab: FC<SettingsTabProps> = ({ root, isSaving, onSave }) => {
  const initialModelId: EvalModelId = root.model_id ?? DEFAULT_EVAL_MODEL_ID;

  const [name, setName] = useState(root.name);
  const [description, setDescription] = useState(root.description ?? '');
  const [archived, setArchived] = useState(Boolean(root.archived));
  const [modelId, setModelId] = useState<EvalModelId>(initialModelId);

  useEffect(() => {
    setName(root.name);
    setDescription(root.description ?? '');
    setArchived(Boolean(root.archived));
    setModelId(root.model_id ?? DEFAULT_EVAL_MODEL_ID);
  }, [root.id, root.name, root.description, root.archived, root.model_id]);

  const isDirty =
    name.trim() !== root.name ||
    (description.trim() || undefined) !== root.description ||
    archived !== Boolean(root.archived) ||
    modelId !== initialModelId;

  const isValid = name.trim().length > 0;

  const handleSave = () => {
    const payload: Omit<UpdateEvalConfigMetaPayload, 'operation'> = {};
    if (name.trim() !== root.name) payload.name = name.trim();
    if ((description.trim() || undefined) !== root.description) payload.description = description.trim() || undefined;
    if (archived !== Boolean(root.archived)) payload.archived = archived;
    if (modelId !== initialModelId) payload.model_id = modelId;
    void onSave(payload);
  };

  const expectations = getWorkflowPackExpectations(root.workflow_type);

  return (
    <div className={cardClasses}>
      <div className={cardHeaderClasses}>
        <h2 className={sectionHeadingClasses}>Settings</h2>
      </div>
      <div className={cardBodyClasses}>
        <InputText
          labelText="Name"
          value={name}
          isRequired
          onChange={(e) => setName(e.target.value)}
          dataTestId="text:eval-config-name"
        />

        <div className="flex flex-col gap-1">
          <span className="text-info-700 text-sm font-medium">Workflow type</span>
          <div className="border-info-200 text-info-900 bg-info-50 rounded-sm border px-3 py-2 text-sm">
            {getWorkflowTypeLabel(root.workflow_type)}
          </div>
          <p className={subtleTextClasses}>
            {expectations.description} Workflow type is fixed at creation. Create a new config to change it.
          </p>
        </div>

        <InputSelect<EvalModelId>
          labelText="Model"
          value={modelId}
          onChange={(value) => {
            if (value) setModelId(value);
          }}
          isSearchable={false}
          options={EVAL_MODEL_ID_OPTIONS}
          dataTestId="select:eval-config-model-id"
        />
        <p className={`${subtleTextClasses} -mt-3`}>
          Changing the model affects future runs only. In-flight and historical runs keep the model they started with.
        </p>

        <InputTextarea
          labelText="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          dataTestId="textarea:eval-config-description"
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={archived}
            onChange={(e) => setArchived(e.target.checked)}
            data-testid="checkbox:eval-config-archived"
          />
          <span className="text-info-700">Archived</span>
        </label>

        <div className="mt-2 flex justify-end">
          <Button
            kind="primary"
            onClick={handleSave}
            isDisabled={!isDirty || !isValid || isSaving}
            loading={isSaving}
            className="h-9.5"
            data-testid="button:save-settings"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
